import { Currency } from 'api/account';
import { HttpClient } from 'api/http-client';
import { ProjectFinancialsContractType } from 'api/project';
import { LinkedResource } from 'api/tasks';
import { IntegrationField } from '../types';
import {
  CreateAutomationPayload,
  SalesforceActions,
  SalesforceAutomation,
  SalesforceConfig,
  SalesforceFieldMapping,
  SalesforceIntegrationProvider,
  SalesforceObject,
  SalesforceResourceMapping,
  SfInstallations,
  UpdateAutomationPayload,
} from './types';

const base = '/integrations/salesforce';

interface GetAuthDetailsPayload {
  params?: Record<any, any>;
}

class SalesforceHTTPClient extends HttpClient {
  public getAuthDetails = ({
    params,
  }: GetAuthDetailsPayload): Promise<SalesforceIntegrationProvider> =>
    this.get<SalesforceIntegrationProvider>(
      '/integration-providers/salesforce/meta',
      {
        params,
      },
    );

  public disconnectIntegration = (integrationId: number): Promise<void> =>
    this.delete<void>(`${base}/${integrationId}`);

  public getIntegrationConfig = (): Promise<SalesforceConfig[]> =>
    this.get<SalesforceConfig[]>(base);

  public getIntegrationConfigById = (
    integrationId: number,
  ): Promise<SalesforceConfig> =>
    this.get<SalesforceConfig>(`${base}/${integrationId}`);

  public getTriggerObjects = (
    integrationId: number,
  ): Promise<SalesforceObject[]> =>
    this.get<SalesforceObject[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects`,
    );

  public getAutomationTriggerFields = (
    integrationId: number,
    salesforceObject: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/trigger-condition-fields`,
    );

  public getAutomationMatchFields = (
    integrationId: number,
    salesforceObject: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/matching-condition-fields`,
    );

  public getSalesforceActionFields = (
    integrationId: number,
    salesforceObject: string,
    actionType: SalesforceActions,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/action-objects/${actionType}/action-input-fields`,
    );

  public getAutomations = (): Promise<SalesforceAutomation[]> =>
    this.get('/automations');

  /**
   * @deprecated
   */
  public getAutomationById = (
    automationId: number,
  ): Promise<SalesforceAutomation> => this.get(`/automations/${automationId}`);

  /**
   * @deprecated
   */
  public createAutomation = (
    payload: CreateAutomationPayload,
  ): Promise<SalesforceAutomation> => this.post('/automations', payload);

  /**
   * @deprecated
   */
  public updateAutomation = (
    payload: UpdateAutomationPayload,
  ): Promise<SalesforceAutomation> =>
    this.put(`/automations/${payload.automationId}`, payload);

  /**
   * @deprecated
   */
  public deleteAutomation = (automaionId: number): Promise<void> =>
    this.delete(`/automations/${automaionId}`);

  public getTemplatePlaceholders = (
    integrationId: string,
    templateId: any,
    salesforceObject: string | number | undefined,
  ): Promise<IntegrationField[]> =>
    this.get(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/dynamic-action-input-fields?templateId=${templateId}`,
    );

  public getFinancialsFields = (
    integrationId: string,
    billingMethod: ProjectFinancialsContractType,
    currencyCode: Currency['currencyCode'],
    salesforceObject: string | number | undefined,
  ): Promise<IntegrationField[]> =>
    this.get(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/dynamic-action-input-fields?financialContractType=${billingMethod}&currencyCode=${currencyCode}`,
    );

  /**
   * @deprecated
   */
  public reorderAutomation = (
    automationId: number,
    automationNewOrder: number,
  ): Promise<SalesforceAutomation[]> =>
    this.put(`/automations/${automationId}/reorder`, {
      order: automationNewOrder,
    });

  public getResourceMappings = (
    integrationId: number,
  ): Promise<SalesforceResourceMapping[]> =>
    this.get<SalesforceResourceMapping[]>(
      `${base}/${integrationId}/resource-mapping-config`,
    );

  public getExternalResourceTypes = (
    integrationId: number,
  ): Promise<SalesforceObject[]> =>
    this.get<SalesforceObject[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types`,
    );

  public getExternalResourceFields = (
    integrationId: number,
    externalResourceType: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types/${externalResourceType}/fields`,
    );

  public getNativeResourceTypes = (
    integrationId: number,
  ): Promise<SalesforceResourceMapping[]> =>
    this.get<SalesforceResourceMapping[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types`,
    );

  public getNativeResourceFields = (
    integrationId: number,
    nativeResourceType: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${nativeResourceType}/fields`,
    );

  public getMappableNativeResourceFields = (
    integrationId: number,
    nativeResourceType: string,
    externalResourceType: string,
    externalResourceFieldId: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${nativeResourceType}/mappable-fields?externalResourceType=${externalResourceType}&externalFieldId=${externalResourceFieldId}`,
    );

  public createFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    payload: SalesforceFieldMapping,
  ): Promise<SalesforceFieldMapping> =>
    this.post(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config`,
      payload,
    );

  public deleteFieldMapping = (
    integrationId: number,
    resourceId: number,
    fieldMappingId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}/field-mapping-config/${fieldMappingId}`,
    );

  public updateFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    fieldMappingId: number,
    payload: SalesforceFieldMapping,
  ): Promise<SalesforceFieldMapping> =>
    this.put(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config/${fieldMappingId}`,
      payload,
    );

  public createResourceObject = (
    integrationId: number,
    payload: AtLeast<
      SalesforceResourceMapping,
      | 'externalIntegrationType'
      | 'externalResourceType'
      | 'nativeResourceType'
      | 'syncType'
      | 'enabled'
    >,
  ): Promise<SalesforceResourceMapping> =>
    this.post(`${base}/${integrationId}/resource-mapping-config/`, payload);

  public deleteResourceObject = (
    integrationId: number,
    resourceId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}`,
    );

  public linkSalesforceObject = (
    integrationId,
    payload,
  ): Promise<LinkedResource> =>
    this.post(`${base}/${integrationId}/api/sobject/resource-mapping`, payload);

  public getSalesforceObjectWithProject = (
    integrationId,
    externalResourceId,
    externalResourceType,
  ): Promise<any> =>
    this.get(
      `${base}/${integrationId}/api/sobject/${externalResourceType}/${externalResourceId}`,
    );

  public unLinkSalesforceObjectWithProject = (
    integrationId,
    resourceMappingId,
  ): Promise<SalesforceObject> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping/${resourceMappingId}`,
    );

  public getSalesForceInstallations(
    integrationId: number,
  ): Promise<SfInstallations[]> {
    return this.get(`${base}/${integrationId}/app-installations`);
  }
}

export const SalesforceAPI = new SalesforceHTTPClient();
